import { useState, useCallback, useEffect, useRef } from "react";
import { Typography } from "../components/atoms/Typography";
import { Input } from "../components/atoms/Input";
import { UserAdminRolesDrawer } from "../components/organisms/UserAdminRolesDrawer";
import { UsersTable } from "../components/organisms/UsersTable";
import { useAuthSession } from "../contexts/AuthSessionContext";
import { Search } from "lucide-react";
import type { ManagedUserRow } from "../types/userManagement";
import { hasSuperAdminRole } from "../utils/sessionModuleAccess";
import { listManagedUsers, updateManagedUserAdminRoles } from "../services/userManagementService";

const debounceMs = 400;

export const UserManagementPage = () => {
  const { adminRoles, user } = useAuthSession();
  const canEditUserRoles = hasSuperAdminRole(adminRoles);

  const [users, setUsers] = useState<ManagedUserRow[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [rolesDrawerUser, setRolesDrawerUser] = useState<ManagedUserRow | null>(null);
  const searchTimerRef = useRef<number | null>(null);


  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await listManagedUsers({
        q: appliedQuery,
        limit: perPage,
        offset: (page - 1) * perPage
      });
      setUsers(result.items);
      setTotalRows(result.total);
    } catch {
      setError("Unable to load users. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [appliedQuery, page, perPage]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    setPage(1);
    if (searchTimerRef.current) {
      window.clearTimeout(searchTimerRef.current);
    }
    searchTimerRef.current = window.setTimeout(() => {
      setAppliedQuery(value);
    }, debounceMs);
  }, []);

  return (
    <section className="page-section">
      <div className="page-header">
        <div>
          <Typography as="h1" variant="h1">
            User Management
          </Typography>
          <p className="page-subtitle">
            Manage users and their access permissions
          </p>
        </div>
      </div>

      <div className="page-toolbar">
        <div className="search-wrapper">
          <Search className="search-icon" />
          <Input
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search by User Name or Email..."
            aria-label="Search by User Name or Email"
            className="bg-white pl-10"
          />
        </div>
      </div>

      {error && <div className="alert-error">{error}</div>}

      <UsersTable
        users={users}
        totalRows={totalRows}
        isLoading={isLoading}
        page={page}
        perPage={perPage}
        onChangePage={(p) => setPage(p)}
        onChangeRowsPerPage={(newPer) => {
          setPerPage(newPer);
          setPage(1);
        }}
        canEditRoles={canEditUserRoles}
        onEditRoles={(row) => setRolesDrawerUser(row)}
      />

      <UserAdminRolesDrawer
        isOpen={rolesDrawerUser !== null}
        user={rolesDrawerUser}
        currentUserId={user?.id ?? null}
        onClose={() => setRolesDrawerUser(null)}
        onSave={async (userId, adminRoles) => {
          await updateManagedUserAdminRoles(userId, adminRoles);
          await loadUsers();
        }}
      />
    </section>
  );
};
